import React from "react";
import {
  Inbox,
  MessagesSquare,
  Kanban,
  Route,
  Smartphone,
  Archive,
  LayoutDashboard,
} from "lucide-react";

const BENEFITS = [
  { Icon: Inbox,          text: "See every new enquiry in one place." },
  { Icon: MessagesSquare, text: "Read and continue LEO conversations." },
  { Icon: Kanban,         text: "Track opportunities from enquiry to quote to completed job." },
  { Icon: Route,          text: "Move customers through a simple visual pipeline." },
  { Icon: Smartphone,     text: "Access customer information from your phone or computer." },
  { Icon: Archive,        text: "Keep old opportunities organised for future follow-up." },
];

const JobHub = () => {
  return (
    <section id="job-hub" className="scroll-mt-20 bg-white py-14 sm:py-20">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          {/* LEFT — copy + benefits */}
          <div className="lg:col-span-7">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full bg-brand-blue/10 px-3.5 py-1.5 text-xs font-black uppercase tracking-widest text-brand-blue">
              <LayoutDashboard className="h-3.5 w-3.5" /> Manage Everything In One Place
            </p>
            <h2 className="text-3xl font-black uppercase leading-[1.05] tracking-tight text-brand-black sm:text-4xl lg:text-5xl">
              Manage Everything From The <span className="text-brand-blue">7FC Job Hub</span>.
            </h2>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[#374151] sm:text-lg">
              Every new enquiry, LEO conversation and quoting opportunity is organised inside your 7FC Job Hub.
            </p>
            <p className="mt-3 max-w-xl text-base leading-relaxed text-[#374151] sm:text-lg">
              Use it from your phone or computer to view conversations, track opportunities and see exactly where every potential job sits.
            </p>

            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              {BENEFITS.map(({ Icon, text }) => (
                <li
                  key={text}
                  className="flex items-start gap-3 rounded-2xl bg-gray-50 p-4 ring-1 ring-gray-200"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-brand-blue ring-1 ring-brand-blue/20">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-medium leading-snug text-brand-black">
                    {text}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-8 rounded-2xl bg-blue-50 px-4 py-4 text-center text-xs font-black uppercase leading-relaxed tracking-wider text-brand-black shadow-sm ring-1 ring-brand-blue/20 sm:text-sm">
              Meta ads generate the enquiries. <span className="text-brand-blue">LEO follows them up.</span>{" "}
              Your 7FC Job Hub keeps everything organised.
            </p>
          </div>

          {/* RIGHT — mocked mobile-app style card of the Job Hub */}
          <div className="lg:col-span-5">
            <div className="mx-auto max-w-sm rounded-[36px] bg-brand-black p-2 shadow-2xl ring-4 ring-gray-200/50 sm:max-w-md">
              <div className="overflow-hidden rounded-[28px] bg-white">
                <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                  <span className="text-[11px] font-black uppercase tracking-widest text-brand-grayMid">
                    7FC Job Hub
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3 text-center">
                  <div>
                    <p className="text-lg font-black text-brand-black">12</p>
                    <p className="text-[9px] font-black uppercase tracking-widest text-brand-grayMid">New</p>
                  </div>
                  <div>
                    <p className="text-lg font-black text-brand-blue">8</p>
                    <p className="text-[9px] font-black uppercase tracking-widest text-brand-grayMid">Quoting</p>
                  </div>
                  <div>
                    <p className="text-lg font-black text-emerald-600">5</p>
                    <p className="text-[9px] font-black uppercase tracking-widest text-brand-grayMid">Booked</p>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {[
                    { name: "Sarah T.", job: "House Wash · 4br", stage: "New", tint: "bg-emerald-100 text-emerald-800" },
                    { name: "Ben M.",   job: "Roof Wash · Tile", stage: "Quoting", tint: "bg-blue-100 text-blue-800" },
                    { name: "Priya K.", job: "Pressure Clean · Driveway", stage: "Quoting", tint: "bg-blue-100 text-blue-800" },
                    { name: "Tom L.",   job: "House + Gutters", stage: "Booked", tint: "bg-amber-100 text-amber-900" },
                  ].map((row) => (
                    <div key={row.name} className="flex items-center justify-between gap-2 px-4 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-brand-black">{row.name}</p>
                        <p className="truncate text-[11px] text-brand-grayMid">{row.job}</p>
                      </div>
                      <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-black uppercase tracking-wider ${row.tint}`}>
                        {row.stage}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JobHub;
